import {
	Container,
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	Icon,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	Price,
	type PriceProps,
	Rating,
	type RatingProps,
	Tag,
	type TagProps,
	Text
} from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/app/utilities/className";
import type { SeasonTheme } from "@/app/utilities/types";

import * as styles from "../shared.module.scss";

export interface FeaturedProductProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> {
	theme?: SeasonTheme;
	mainProduct: {
		image: ImageProps<ImageComponentProps>;
		tag: TagProps;
		title: string;
		price?: PriceProps;
		rating?: RatingProps;
		description: string;
		cta: LinkProps<LinkComponentProps>;
	};
	relatedRecipes: {
		title: string;
		recipeCards: {
			image: ImageProps<ImageComponentProps>;
			title: string;
			prepTime: string;
			description: string;
			cta: LinkProps<LinkComponentProps>;
		}[];
	};
}

/**
 * Featured Product component
 */
export function FeaturedProduct<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>({
	theme = "standard",
	...props
}: FeaturedProductProps<ImageComponentProps, LinkComponentProps>): JSX.Element {
	return (
		<div
			{...classNameHelper(
				styles["featured"],
				theme && styles[`featured--${theme}`]
			)}
		>
			<Icon
				name="featured-background"
				className={styles["featured__background"]}
			/>
			<Container>
				<article className={styles["featured__main"]}>
					<Image
						{...props.mainProduct?.image}
						className={styles["featured__main-image"]}
						alt={
							props.mainProduct?.title || "Featured Product Image"
						}
					/>
					<div>
						<Tag {...props.mainProduct?.tag} />
						<Heading
							tag="h2"
							styledAs="h1"
							className={styles["featured__main-title"]}
						>
							{props.mainProduct?.title}
						</Heading>
						<div className={styles["featured__main-details"]}>
							{props.mainProduct?.price && (
								<Price {...props.mainProduct.price} />
							)}
							{props.mainProduct?.rating && (
								<Rating {...props.mainProduct.rating} />
							)}
						</div>
						<Text
							styledAs="xsr"
							className={styles["featured__main-description"]}
						>
							{props.mainProduct?.description}
						</Text>
						<Link
							{...props.mainProduct?.cta}
							kind="primary"
							className={styles["featured__main-cta"]}
						/>
					</div>
				</article>
				<div className={styles["featured__related-container"]}>
					{props.relatedRecipes?.title && (
						<Heading tag="h2" styledAs="h2">
							{props.relatedRecipes?.title}
						</Heading>
					)}
					<div className={styles["featured__related"]}>
						{props.relatedRecipes?.recipeCards.map(
							(card, index) => (
								<article
									key={index}
									className={styles["featured__related-card"]}
								>
									<Image
										{...card?.image}
										{...classNameHelper(
											styles["featured__related-image"],
											styles[
												"featured__related-image--desktop"
											]
										)}
										alt={
											card.image?.alt ||
											"Featured Related Image"
										}
									/>
									<div
										className={
											styles["featured__related-content"]
										}
									>
										<Heading
											tag="h3"
											styledAs="h3"
											className={
												styles[
													"featured__related-title"
												]
											}
										>
											{card?.title}
										</Heading>
										<Text styledAs="xsr" className="mb-0">
											{card?.prepTime}
										</Text>
										<Text
											styledAs="xsr"
											className={
												styles[
													"featured__related-description"
												]
											}
										>
											{card?.description}
										</Text>
										<Image
											{...card?.image}
											{...classNameHelper(
												styles[
													"featured__related-image"
												],
												styles[
													"featured__related-image--mobile"
												]
											)}
											alt={
												card.image?.alt ||
												"Featured Related Image Mobile"
											}
										/>
										<Link
											className={
												styles["featured__related-cta"]
											}
											{...card?.cta}
											kind="primary"
											size="md"
										/>
									</div>
								</article>
							)
						)}
					</div>
				</div>
			</Container>
		</div>
	);
}
